import {useMutation} from '@tanstack/react-query';

import {useDocumentHistoryQueryCache} from '../../../../providers/version/document-history-query';
import {apiClient} from '../../../../utils/api-client';
import {useSearchDocsCache} from '../sidebar/search-layout/hooks/use-edit-mode-search-query-cache';

import {useCategoriesInfiniteQueryCache} from './categories/use-categories-infinite-query-cache';
import {useDeletedDocumentsQueryCache} from './use-deleted-documents-query';
import {useDocumentQueryCache} from './use-document-query';
import {useLastActionQueryCache} from './use-last-action-query';
import {useOntologyDocsQueryCache} from './use-ontology-docs-query';

function deleteDocumentRequest(accession: string) {
    return apiClient.post({
        path: `editor-documents-service/api/documents/${accession}/trash-bin`
    });
}

/** Discard working copy */
export function useDeleteDocumentMutation() {
    const lastActionQueryCache = useLastActionQueryCache();
    const documentQueryCache = useDocumentQueryCache();
    const documentHistoryQueryCache = useDocumentHistoryQueryCache();
    const documentsQueryCache = useSearchDocsCache();
    const deletedQueryCache = useDeletedDocumentsQueryCache();
    const ontologyDocsQueryCache = useOntologyDocsQueryCache();
    const categoriesQueryCache = useCategoriesInfiniteQueryCache();

    return useMutation({
        mutationFn: async (accession: string) => {
            await deleteDocumentRequest(accession);

            const nextDocument = await documentQueryCache.get(accession);

            if (nextDocument) {
                const categoryNodeIds =
                    ontologyDocsQueryCache.getCategoriesByDocument(nextDocument);
                categoryNodeIds.forEach((categoryNodeId) => {
                    categoriesQueryCache.updateDocumentsCount(categoryNodeId, -1);
                });

                ontologyDocsQueryCache.deleteDocument(nextDocument);
                deletedQueryCache.addDocument(nextDocument);
            } else {
                await ontologyDocsQueryCache.invalidate();
                await deletedQueryCache.invalidate();
            }

            await categoriesQueryCache.invalidate();
            await lastActionQueryCache.invalidate(accession, 'DELETE');
            documentHistoryQueryCache.invalidate(accession);
            documentsQueryCache.deleteDocument(accession);
        }
    });
}
